import { useEffect, useRef, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { PHONE_NUMBER } from "../../constants/ProductConstants";
import "./giftBox.css";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const floatingProducts = [
  {
    key: "earrings",
    label: "Earrings",
    note: "Polished studs",
  },
  {
    key: "necklace",
    label: "Necklaces",
    note: "Layered shine",
  },
  {
    key: "bracelet",
    label: "Bracelets",
    note: "Elegant stack",
  },
];

export default function GiftBoxRevealSection() {
  const sceneRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const whatsappMessage =
    "Hi Cherry On Top, I want a customized gift box. Please share packaging options and charges.";
  const whatsappLink = `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(
    whatsappMessage
  )}`;

  useEffect(() => {
    const sceneElement = sceneRef.current;

    if (!sceneElement) {
      return undefined;
    }

    let frameId = null;

    const updateProgress = () => {
      frameId = null;

      const rect = sceneElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const scrollableDistance = Math.max(rect.height - viewportHeight, 1);
      const rawProgress = -rect.top / scrollableDistance;
      const nextProgress = clamp(rawProgress, 0, 1);

      setProgress((current) =>
        Math.abs(current - nextProgress) > 0.002 ? nextProgress : current
      );
    };

    const requestTick = () => {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(updateProgress);
      }
    };

    requestTick();
    window.addEventListener("scroll", requestTick, { passive: true });
    window.addEventListener("resize", requestTick);

    return () => {
      window.removeEventListener("scroll", requestTick);
      window.removeEventListener("resize", requestTick);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const openProgress = clamp((progress - 0.15) / 0.5, 0, 1);
  const floatProgress = clamp((progress - 0.3) / 0.5, 0, 1);
  const easedProgress = 1 - Math.pow(1 - progress, 3);

  return (
    <div className="giftbox-page">
      <section
        ref={sceneRef}
        className="giftbox-scroll-scene"
        style={{
          "--gift-progress": progress.toFixed(3),
          "--gift-eased-progress": easedProgress.toFixed(3),
          "--gift-open-progress": openProgress.toFixed(3),
          "--gift-float-progress": floatProgress.toFixed(3),
        }}
      >
        <div className="giftbox-sticky-stage">
          <div className="giftbox-stage-copy">
            <span className="giftbox-kicker">Gift Box Experience</span>
            <h1>Unbox the moment as you scroll.</h1>
            <p>
              A cinematic reveal designed to feel deliberate, polished, and a
              little magical.
            </p>
          </div>

          <div className="giftbox-stage-visual">
            <div className="giftbox-aura giftbox-aura-left" />
            <div className="giftbox-aura giftbox-aura-right" />

            <div className="giftbox-products-layer" aria-hidden="true">
              {floatingProducts.map((product) => (
                <article
                  key={product.key}
                  className={`giftbox-product giftbox-product-${product.key}`}
                >
                  <div className={`giftbox-jewel giftbox-jewel-${product.key}`}>
                    <span />
                    <span />
                    <span />
                  </div>
                  <div className="giftbox-product-copy">
                    <strong>{product.label}</strong>
                    <small>{product.note}</small>
                  </div>
                </article>
              ))}
            </div>

            <div className="giftbox-shell" aria-hidden="true">
              <div className="giftbox-lid giftbox-lid-left">
                <span className="giftbox-lid-inner" />
              </div>
              <div className="giftbox-lid giftbox-lid-right">
                <span className="giftbox-lid-inner" />
              </div>

              <div className="giftbox-base">
                <div className="giftbox-base-top" />
                <div className="giftbox-ribbon-vertical" />
                <div className="giftbox-ribbon-horizontal" />
                <div className="giftbox-bow giftbox-bow-left" />
                <div className="giftbox-bow giftbox-bow-right" />
                <div className="giftbox-core-glow" />
              </div>
            </div>
          </div>

          <div className="giftbox-progress-copy">
            <span>Scroll to reveal</span>
            <div className="giftbox-progress-track">
              <div
                className="giftbox-progress-bar"
                style={{ transform: `scaleX(${progress.toFixed(3)})` }}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="giftbox-details-section">
        <div className="giftbox-details-shell">
          <div className="giftbox-details-header">
            <span className="giftbox-kicker">Packaging</span>
            <h2>Customized Gift Box</h2>
            <p className="giftbox-details-subtitle">Packaging extra</p>
          </div>

          <div className="giftbox-details-card">
            <p>
              A thoughtful gift is a beautiful way to say, "I&apos;m thinking of
              you." Show someone you care today.
            </p>
            <a
              className="giftbox-whatsapp-cta"
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              aria-label="Chat on WhatsApp for customized gift box"
            >
              <span className="giftbox-whatsapp-icon" aria-hidden="true">
                <FaWhatsapp className="giftbox-whatsapp-glyph" />
              </span>
              <span className="giftbox-whatsapp-text">
                <strong>Chat on WhatsApp</strong>
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}